import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Search, Eye, Loader2, MessageSquare, Pause, Play,
  CheckCircle, XCircle, Clock, CreditCard, Filter, DollarSign,
  User, Send, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const STATUS_CONFIG = {
  pending_payment: { label: 'Pago Pendiente', icon: Clock, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
  active: { label: 'Activo', icon: CheckCircle, color: 'text-green-400 bg-green-500/10 border-green-500/30' },
  in_progress: { label: 'En Progreso', icon: Play, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  completed: { label: 'Completado', icon: CheckCircle, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  cancelled: { label: 'Cancelado', icon: XCircle, color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  paused: { label: 'Pausado', icon: Pause, color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
};

const ContractsAdminPage = () => {
  const [contracts, setContracts] = useState([]);
  const [acceptedQuotes, setAcceptedQuotes] = useState([]);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedContract, setSelectedContract] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [cancellationAction, setCancellationAction] = useState('');
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeView, setActiveView] = useState('contracts'); // 'contracts', 'quotes', or 'cancellations'
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatData, setChatData] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    fetchContracts();
    fetchAcceptedQuotes();
    fetchCancellationRequests();
  }, [statusFilter]);
  
  const fetchContracts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      console.log('Fetching contracts from:', `/admin/contracts${params}`);
      const response = await api.get(`/admin/contracts${params}`);
      console.log('Contracts response:', response.data);
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      setError(errorMsg);
      toast.error(`Error al cargar contratos: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAcceptedQuotes = async () => {
    try {
      // Fetch quotes that are accepted but don't have contracts yet
      const response = await api.get('/admin/quotes?status=accepted');
      setAcceptedQuotes(response.data || []);
    } catch (error) {
      console.error('Error fetching accepted quotes:', error);
    }
  };

  const fetchCancellationRequests = async () => {
    try {
      const response = await api.get('/admin/cancellation-requests');
      setCancellationRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching cancellation requests:', error);
    }
  };

  const handleProcessCancellation = async () => {
    if (!cancellationAction) {
      toast.error('Seleccioná una acción');
      return;
    }
    
    setUpdating(true);
    try {
      await api.put(`/admin/cancellation-requests/${selectedCancellation.id}/process`, {
        action: cancellationAction,
        notes: cancellationNotes.trim()
      });
      toast.success(cancellationAction === 'approved' ? 'Cancelación aprobada' : 'Cancelación rechazada');
      setShowCancellationModal(false);
      fetchCancellationRequests();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setUpdating(false);
    }
  };

  const handleViewChat = async (contractId) => {
    setLoadingChat(true);
    try {
      const response = await api.get(`/admin/chat-monitoring/${contractId}`);
      setChatData(response.data);
      setShowChatModal(true);
    } catch (error) {
      toast.error('Error al cargar el chat');
      console.error(error);
    } finally {
      setLoadingChat(false);
    }
  };

  const fetchContractDetails = async (contractId) => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/admin/contracts/${contractId}`);
      setContractDetails(response.data);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      toast.error('Error al cargar detalles');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOpenDetail = async (contract) => {
    setSelectedContract(contract);
    setShowDetailModal(true);
    await fetchContractDetails(contract.id);
  };

  const handleOpenStatusChange = (contract) => {
    setSelectedContract(contract);
    setNewStatus(contract.status);
    setStatusNotes('');
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || newStatus === selectedContract.status) {
      toast.error('Seleccioná un nuevo estado');
      return;
    }
    
    setUpdating(true);
    try {
      await api.put(`/admin/contracts/${selectedContract.id}/status?status=${newStatus}&notes=${encodeURIComponent(statusNotes)}`);
      toast.success('Estado actualizado');
      setShowStatusModal(false);
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending_payment;
    const Icon = config.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const filteredContracts = contracts.filter(contract => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      contract.press_name?.toLowerCase().includes(term) ||
      contract.producer_name?.toLowerCase().includes(term) ||
      contract.event_name?.toLowerCase().includes(term) ||
      contract.id?.toLowerCase().includes(term)
    );
  });

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white">Gestión de Contratos</h1>
              <p className="text-zinc-500 mt-1">Administra contratos y cotizaciones de prensa</p>
            </div>
            
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-zinc-800 border-zinc-700 w-64"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 w-44">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              variant={activeView === 'contracts' ? 'default' : 'outline'}
              onClick={() => setActiveView('contracts')}
              className={activeView === 'contracts' ? 'bg-blue-500 hover:bg-blue-600' : ''}
            >
              <FileText size={16} className="mr-2" />
              Contratos ({contracts.length})
            </Button>
            <Button
              variant={activeView === 'quotes' ? 'default' : 'outline'}
              onClick={() => setActiveView('quotes')}
              className={activeView === 'quotes' ? 'bg-green-500 hover:bg-green-600' : ''}
            >
              <CreditCard size={16} className="mr-2" />
              Cotizaciones Aceptadas ({acceptedQuotes.filter(q => !q.has_contract).length})
            </Button>
            <Button
              variant={activeView === 'cancellations' ? 'default' : 'outline'}
              onClick={() => setActiveView('cancellations')}
              className={activeView === 'cancellations' ? 'bg-amber-500 hover:bg-amber-600 text-black' : ''}
            >
              <XCircle size={16} className="mr-2" />
              Solicitudes Cancelación ({cancellationRequests.filter(c => c.status === 'pending').length})
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Contratos', value: contracts.length, color: 'text-zinc-400' },
              { label: 'Activos', value: contracts.filter(c => c.status === 'active').length, color: 'text-green-400' },
              { label: 'En Progreso', value: contracts.filter(c => c.status === 'in_progress').length, color: 'text-blue-400' },
              { label: 'Completados', value: contracts.filter(c => c.status === 'completed').length, color: 'text-emerald-400' },
              { label: 'Cotiz. Pendientes', value: acceptedQuotes.filter(q => !q.has_contract).length, color: 'text-yellow-400' },
            ].map((stat, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-zinc-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Content based on active view */}
          {activeView === 'contracts' ? (
            <>
              {/* Contracts List */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="animate-spin text-blue-500" size={32} />
                </div>
              ) : error ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-12 text-center">
                  <XCircle className="mx-auto mb-4 text-red-500" size={48} />
                  <p className="text-red-400 font-medium">Error al cargar contratos</p>
                  <p className="text-zinc-500 text-sm mt-2">{error}</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                onClick={fetchContracts}
              >
                Reintentar
              </Button>
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
              <FileText className="mx-auto mb-4 text-zinc-600" size={48} />
              <p className="text-zinc-500">No hay contratos</p>
              <p className="text-zinc-600 text-sm mt-2">
                Los contratos se crean cuando una productora inicia el pago de una cotización aceptada
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredContracts.map((contract) => (
                <motion.div
                  key={contract.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                  data-testid={`admin-contract-${contract.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-white font-bold">{contract.press_name}</h3>
                        <span className="text-zinc-500">→</span>
                        <span className="text-zinc-400">{contract.producer_name}</span>
                        {getStatusBadge(contract.status)}
                      </div>
                      {contract.event_name && (
                        <p className="text-zinc-500 text-sm mt-1">Evento: {contract.event_name}</p>
                      )}
                      <p className="text-zinc-600 text-xs mt-1">
                        ID: {contract.id.slice(-8).toUpperCase()} • 
                        Creado: {new Date(contract.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-white font-bold text-lg">
                          ${contract.total_amount?.toLocaleString()}
                        </p>
                        <p className="text-zinc-500 text-xs">
                          Comisión: ${contract.platform_fee?.toLocaleString()}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleViewChat(contract.id)}
                          disabled={loadingChat}
                          data-testid="view-chat-btn"
                        >
                          <MessageSquare size={14} className="mr-1" />
                          Chat
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenDetail(contract)}
                        >
                          <Eye size={14} className="mr-1" />
                          Ver
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                          onClick={() => handleOpenStatusChange(contract)}
                        >
                          Cambiar Estado
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
            </>
          ) : (
            /* Accepted Quotes View */
            <div className="space-y-4">
              {acceptedQuotes.filter(q => !q.has_contract).length === 0 ? (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                  <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                  <p className="text-zinc-400">No hay cotizaciones pendientes de pago</p>
                  <p className="text-zinc-600 text-sm mt-2">
                    Todas las cotizaciones aceptadas ya tienen contrato creado
                  </p>
                </div>
              ) : (
                acceptedQuotes.filter(q => !q.has_contract).map((quote) => (
                  <motion.div
                    key={quote.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-zinc-900/50 border border-green-500/20 rounded-xl p-5"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-white font-bold">{quote.press_name}</h3>
                          <span className="text-zinc-500">→</span>
                          <span className="text-zinc-400">{quote.producer_name}</span>
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs border text-green-400 bg-green-500/10 border-green-500/30">
                            <CheckCircle size={12} />
                            Aceptada
                          </span>
                        </div>
                        {quote.event_name && (
                          <p className="text-zinc-500 text-sm mt-1">Evento: {quote.event_name}</p>
                        )}
                        <p className="text-zinc-600 text-xs mt-1">
                          ID: {quote.id?.slice(-8).toUpperCase()} • 
                          Creado: {new Date(quote.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-white font-bold text-lg">
                            ${quote.response?.quote_amount?.toLocaleString() || quote.total_amount?.toLocaleString() || '0'}
                          </p>
                          <p className="text-yellow-400 text-xs">
                            Esperando pago del productor
                          </p>
                        </div>
                        
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={async () => {
                            if (window.confirm('¿Crear contrato manualmente sin pago? El contrato quedará activo inmediatamente.')) {
                              try {
                                await api.post(`/admin/quotes/${quote.id}/create-contract`);
                                toast.success('Contrato creado exitosamente');
                                fetchContracts();
                                fetchAcceptedQuotes();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Error al crear contrato');
                              }
                            }
                          }}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Activar Contrato
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Cancellation Requests View */}
          {activeView === 'cancellations' && (
            <div className="space-y-4">
              {cancellationRequests.length === 0 ? (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                  <XCircle className="mx-auto text-zinc-600 mb-4" size={48} />
                  <p className="text-zinc-400">No hay solicitudes de cancelación</p>
                </div>
              ) : (
                cancellationRequests.map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-zinc-900/50 border rounded-xl p-5 ${
                      request.status === 'pending' 
                        ? 'border-amber-500/30' 
                        : request.status === 'approved' 
                          ? 'border-green-500/30' 
                          : 'border-red-500/30'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                          <h3 className="text-white font-bold">{request.requester_name}</h3>
                          <span className="text-zinc-500 text-sm">
                            ({request.requester_type === 'producer' ? 'Productora' : 'Prensa'})
                          </span>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                            request.status === 'pending' 
                              ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' 
                              : request.status === 'approved'
                                ? 'text-green-400 bg-green-500/10 border-green-500/30'
                                : 'text-red-400 bg-red-500/10 border-red-500/30'
                          }`}>
                            {request.status === 'pending' ? <Clock size={12} /> : 
                             request.status === 'approved' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                            {request.status === 'pending' ? 'Pendiente' : 
                             request.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                          </span>
                        </div>
                        <p className="text-amber-400 text-sm font-medium mb-1">
                          Motivo: {request.reason_label}
                        </p>
                        {request.details && (
                          <p className="text-zinc-400 text-sm mb-2">"{request.details}"</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                          <span>Contrato: {request.contract_id?.slice(-8).toUpperCase()}</span>
                          <span>Prensa: {request.press_name}</span>
                          <span>Productora: {request.producer_name}</span>
                          {request.hours_until_event !== null && (
                            <span className={request.meets_time_requirement ? 'text-green-400' : 'text-red-400'}>
                              {Math.round(request.hours_until_event)}hs hasta evento
                              {!request.meets_time_requirement && ' (No cumple)'}
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-600 text-xs mt-2">
                          Creado: {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                      
                      {request.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-black"
                          onClick={() => {
                            setSelectedCancellation(request);
                            setCancellationAction('');
                            setCancellationNotes('');
                            setShowCancellationModal(true);
                          }}
                        >
                          <Eye size={14} className="mr-1" />
                          Procesar
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Detalles del Contrato</DialogTitle>
            </DialogHeader>
            
            {loadingDetails ? (
              <div className="py-12 text-center">
                <Loader2 className="animate-spin text-blue-500 mx-auto" size={32} />
              </div>
            ) : contractDetails && (
              <div className="space-y-6 py-4">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <span className="text-zinc-400">Estado:</span>
                  {getStatusBadge(contractDetails.status)}
                </div>
                
                {/* Parties */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-xs mb-2">PRENSA</p>
                    <p className="text-white font-bold">{contractDetails.press?.media_name}</p>
                    <p className="text-zinc-400 text-sm">{contractDetails.press?.email}</p>
                    <p className="text-zinc-500 text-sm">{contractDetails.press?.phone}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-xs mb-2">PRODUCTORA</p>
                    <p className="text-white font-bold">{contractDetails.producer?.business_name}</p>
                    <p className="text-zinc-400 text-sm">{contractDetails.producer?.email}</p>
                    <p className="text-zinc-500 text-sm">{contractDetails.producer?.phone}</p>
                  </div>
                </div>
                
                {/* Amounts */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <p className="text-zinc-500 text-xs mb-2">DESGLOSE FINANCIERO</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-zinc-500 text-xs">Cotización</p>
                      <p className="text-white font-bold">${contractDetails.quote_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Comisión ({contractDetails.platform_fee_percent}%)</p>
                      <p className="text-yellow-400 font-bold">${contractDetails.platform_fee?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Total</p>
                      <p className="text-green-400 font-bold">${contractDetails.total_amount?.toLocaleString()}</p>
                    </div>
                  </div>
                  {contractDetails.press_absorbs_fee && (
                    <p className="text-orange-400 text-xs mt-3 text-center">
                      * La prensa absorbe la comisión
                    </p>
                  )}
                </div>
                
                {/* Chat Info */}
                {contractDetails.chat && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <p className="text-blue-400 font-medium flex items-center gap-2 mb-2">
                      <MessageSquare size={16} />
                      Chat del Contrato
                    </p>
                    <p className="text-zinc-400 text-sm">
                      {contractDetails.chat.message_count} mensajes totales
                    </p>
                    {contractDetails.chat.recent_messages?.length > 0 && (
                      <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                        {contractDetails.chat.recent_messages.slice(0, 5).map((msg, i) => (
                          <div key={i} className="bg-zinc-800/50 rounded-lg p-2 text-sm">
                            <p className="text-zinc-500 text-xs">{msg.sender_type}</p>
                            <p className="text-zinc-300">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Admin Notes */}
                {contractDetails.admin_notes && (
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-xs mb-1">NOTAS ADMIN</p>
                    <p className="text-zinc-300">{contractDetails.admin_notes}</p>
                  </div>
                )}
                
                {/* Timestamps */}
                <div className="text-zinc-500 text-xs space-y-1">
                  <p>Creado: {new Date(contractDetails.created_at).toLocaleString()}</p>
                  {contractDetails.completed_at && (
                    <p>Completado: {new Date(contractDetails.completed_at).toLocaleString()}</p>
                  )}
                  {contractDetails.cancelled_at && (
                    <p>Cancelado: {new Date(contractDetails.cancelled_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Status Change Modal */}
        <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Cambiar Estado del Contrato</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Nuevo Estado</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-400">Notas (opcional)</Label>
                <Textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Motivo del cambio de estado..."
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowStatusModal(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={handleUpdateStatus}
                  disabled={updating}
                >
                  {updating ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <CheckCircle size={16} className="mr-2" />
                  )}
                  Actualizar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancellation Request Modal */}
        <Dialog open={showCancellationModal} onOpenChange={setShowCancellationModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <XCircle className="text-amber-400" size={20} />
                Procesar Solicitud de Cancelación
              </DialogTitle>
            </DialogHeader>
            
            {selectedCancellation && (
              <div className="space-y-4 py-4">
                {/* Request Info */}
                <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Solicitante:</span>
                    <span className="text-white font-medium">{selectedCancellation.requester_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Tipo:</span>
                    <span className="text-zinc-300">{selectedCancellation.requester_type === 'producer' ? 'Productora' : 'Prensa'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Motivo:</span>
                    <span className="text-amber-400">{selectedCancellation.reason_label}</span>
                  </div>
                  {selectedCancellation.details && (
                    <div>
                      <span className="text-zinc-500 text-sm block mb-1">Detalles:</span>
                      <p className="text-zinc-300 text-sm bg-zinc-900 p-2 rounded">{selectedCancellation.details}</p>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Horas requeridas:</span>
                    <span className="text-zinc-300">{selectedCancellation.hours_required}hs</span>
                  </div>
                  {selectedCancellation.hours_until_event !== null && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500 text-sm">Horas hasta evento:</span>
                      <span className={selectedCancellation.meets_time_requirement ? 'text-green-400' : 'text-red-400'}>
                        {Math.round(selectedCancellation.hours_until_event)}hs
                        {!selectedCancellation.meets_time_requirement && ' (No cumple)'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Time Requirement Warning */}
                {!selectedCancellation.meets_time_requirement && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <Clock size={14} />
                      La solicitud no cumple con el tiempo de anticipación requerido
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-zinc-400">Acción</Label>
                  <Select value={cancellationAction} onValueChange={setCancellationAction}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue placeholder="Seleccionar acción" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="approved">
                        <span className="text-green-400">Aprobar Cancelación</span>
                      </SelectItem>
                      <SelectItem value="rejected">
                        <span className="text-red-400">Rechazar Solicitud</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Notas del Admin (opcional)</Label>
                  <Textarea
                    value={cancellationNotes}
                    onChange={(e) => setCancellationNotes(e.target.value)}
                    placeholder="Motivo de la decisión..."
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowCancellationModal(false)}
                  >
                    Cerrar
                  </Button>
                  <Button 
                    className={`flex-1 ${cancellationAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                    onClick={handleProcessCancellation}
                    disabled={updating || !cancellationAction}
                  >
                    {updating ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : cancellationAction === 'approved' ? (
                      <CheckCircle size={16} className="mr-2" />
                    ) : (
                      <XCircle size={16} className="mr-2" />
                    )}
                    {cancellationAction === 'approved' ? 'Aprobar' : cancellationAction === 'rejected' ? 'Rechazar' : 'Procesar'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Chat Modal */}
        <Dialog open={showChatModal} onOpenChange={setShowChatModal}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <MessageSquare className="text-[#D4AF37]" size={20} />
                Chat del Contrato
              </DialogTitle>
            </DialogHeader>
            
            {chatData && (
              <div className="space-y-4">
                {/* Contract Info */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Prensa:</span>
                    <span className="text-white ml-2 font-medium">{chatData.contract.press_name}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Productora:</span>
                    <span className="text-white ml-2 font-medium">{chatData.contract.producer_name}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Evento:</span>
                    <span className="text-white ml-2">{chatData.contract.event_title}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Estado:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      chatData.contract.status === 'active' ? 'bg-green-500/20 text-green-400' :
                      chatData.contract.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {chatData.contract.status}
                    </span>
                  </div>
                </div>

                {/* Flagged Messages Warning */}
                {chatData.flagged_messages?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-3">
                    <AlertTriangle className="text-red-400 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-red-400 font-medium text-sm">
                        {chatData.flagged_messages.length} mensaje(s) marcado(s)
                      </p>
                      <p className="text-red-400/70 text-xs mt-1">
                        Se detectaron mensajes que podrían violar las políticas
                      </p>
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 max-h-[400px] overflow-y-auto space-y-3">
                  {chatData.messages?.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No hay mensajes en este chat</p>
                  ) : (
                    chatData.messages?.map((msg, idx) => {
                      const isSystem = msg.sender_type === 'system';
                      const isPress = msg.sender_type === 'press';
                      const isFlagged = chatData.flagged_messages?.some(f => f.message_id === msg.id);
                      
                      return (
                        <div 
                          key={msg.id || idx}
                          className={`flex ${isSystem ? 'justify-center' : isPress ? 'justify-start' : 'justify-end'}`}
                        >
                          {isSystem ? (
                            <div className="bg-zinc-800 text-zinc-400 text-xs px-4 py-2 rounded-full flex items-center gap-2">
                              <Clock size={12} />
                              {msg.content}
                            </div>
                          ) : (
                            <div className={`max-w-[70%] ${isFlagged ? 'ring-2 ring-red-500/50 rounded-xl' : ''}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${isPress ? 'text-blue-400' : 'text-[#D4AF37]'}`}>
                                  {isPress ? chatData.contract.press_name : chatData.contract.producer_name}
                                </span>
                                <span className="text-zinc-600 text-[10px]">
                                  {new Date(msg.created_at).toLocaleString('es-AR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                                {isFlagged && (
                                  <span className="text-red-400 text-[10px] flex items-center gap-1">
                                    <AlertTriangle size={10} /> Marcado
                                  </span>
                                )}
                              </div>
                              <div className={`px-4 py-2 rounded-xl ${
                                isPress 
                                  ? 'bg-zinc-800 text-white' 
                                  : 'bg-[#D4AF37]/20 text-white'
                              }`}>
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setShowChatModal(false)}>
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default ContractsAdminPage;
